/**
 * SMS — App 外殼（P1.5-A；`decisions/015`）
 *
 * 版面：**頂部幼標題列（App 名 ＋ 右上角 ⚙）** ＋ 內容區 ＋ **底部角色 Tab**
 *   · 手機／桌面同一版（單欄；`ui-spec` §7.1）
 *   · ⚙ 用 onclick 彈出選單：語言｜**改密碼**｜登出｜（依角色）更多入口
 *   · 底部 tab 由 `src/lib/role-tabs.ts` 出（**「邊個角色見到邊 4 個功能」唯一真相源**）
 *
 * ⚠️ 前端 tab／入口**唔係**權限：後端每個 route 自己 `guard()` 再擋一次。
 * ⚠️ 顏色一律 `var(--color-*)`；文字一律走 i18n（三語齊）。
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import RoleTabBar, { TAB_BAR_SPACER } from '@coeldery/role-tab-bar';
import { btnGhost, btnPrimary, card, h1, header, headerInner } from '@coeldery/ui-kit';
import { activeTabId, tabsFor } from '../lib/role-tabs';
import { useNewVersion } from '../lib/use-new-version';
import type { Me } from '../lib/api';

export interface AppShellProps {
  user: Me;
  onLogout: () => void;
  /** 語言切換器（由 App 傳入；本元件唔自己砌 i18n 狀態） */
  langSwitch: React.ReactNode;
  children: React.ReactNode;
}

/** 選單一行（內部用） */
function MenuLink({ to, label, onGo }: { to: string; label: string; onGo: () => void }) {
  return (
    <Link
      to={to}
      onClick={onGo}
      style={{
        display: 'block',
        padding: '12px 14px',
        minHeight: '44px',
        color: 'var(--color-text)',
        textDecoration: 'none',
        borderBottom: '1px solid var(--color-divider)',
        fontSize: '16px',
      }}
    >
      {label}
    </Link>
  );
}

export default function AppShell({ user, onLogout, langSwitch, children }: AppShellProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);

  // 換頁就收埋選單（唔好留喺半開狀態）
  useEffect(() => setMenu(false), [pathname]);

  const isAdmin = user.role === 'admin';
  const isInternal = user.role === 'staff' || user.role === 'admin';
  const tabs = tabsFor(user.role);
  const active = activeTabId(user.role, pathname);
  /** ⭐ 偵測到新版本 → 彈提示（唔自動 reload，免得打斷填表單） */
  const stale = useNewVersion();

  return (
    <>
      {/* ⭐ 有新版本：頂部提示 ＋ 一撳重載（用戶卡喺舊版係最難查嘅事故 —— 唔好靠佢自己撞） */}
      {stale && (
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 40,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '8px 12px',
            backgroundColor: 'var(--color-warn-bg)',
            color: 'var(--color-warn-text)',
            fontSize: '14px',
          }}
          role="status"
        >
          <span style={{ fontWeight: 700 }}>{t('app.newVersion')}</span>
          <button
            type="button"
            style={{ ...btnPrimary, marginLeft: 'auto', minHeight: '34px' }}
            onClick={() => window.location.reload()}
          >
            {t('common.refresh')}
          </button>
        </div>
      )}

      <header style={header}>
        <div style={headerInner}>
          <div>
            <h1 style={h1}>{t('app.title')}</h1>
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-text-secondary)' }}>
              {user.username} · {t(`role.${user.role}`)}
            </p>
          </div>
          <button
            type="button"
            aria-label={t('tab.menu')}
            aria-expanded={menu}
            onClick={() => setMenu((v) => !v)}
            style={{ ...btnGhost, marginLeft: 'auto', fontSize: '20px', minWidth: '44px' }}
          >
            ⚙
          </button>
        </div>
      </header>

      {/* ⚙ 選單（onclick 彈出；手機貼右邊、桌面貼右上） */}
      {menu && (
        <div
          style={{
            position: 'fixed',
            top: '64px',
            right: '12px',
            width: 'min(320px, calc(100vw - 24px))',
            zIndex: 30,
            ...card,
            padding: 0,
            overflow: 'hidden',
          }}
          role="menu"
        >
          <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--color-divider)' }}>
            <p style={{ margin: '0 0 8px', fontSize: '13px', color: 'var(--color-text-secondary)' }}>
              {t('tab.language')}
            </p>
            {langSwitch}
          </div>
          <MenuLink to="/setup" label={t('tab.account')} onGo={() => setMenu(false)} />
          {(isInternal || user.role === 'logistics') && (
            <MenuLink to="/pending" label={t('tab.todo')} onGo={() => setMenu(false)} />
          )}
          {isInternal && <MenuLink to="/reports" label={t('tab.reports')} onGo={() => setMenu(false)} />}
          {isAdmin && <MenuLink to="/master" label={t('tab.master')} onGo={() => setMenu(false)} />}
          {isAdmin && <MenuLink to="/movements" label={t('tab.ledger')} onGo={() => setMenu(false)} />}
          {isAdmin && <MenuLink to="/settings" label={t('tab.settings')} onGo={() => setMenu(false)} />}
          <button
            type="button"
            onClick={() => {
              setMenu(false);
              onLogout();
            }}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: '12px 14px',
              minHeight: '44px',
              border: 'none',
              background: 'transparent',
              color: 'var(--color-accent)',
              fontSize: '16px',
              cursor: 'pointer',
            }}
          >
            {t('login.logout')}
          </button>
          {/* ⭐ 版本號：對唔上版本時一眼睇到（「撳咗冇反應」第一件事就係查呢個） */}
          <div style={{ padding: '10px 14px', borderTop: '1px solid var(--color-divider)', fontSize: '12px', color: 'var(--color-text-secondary)' }}>
            {t('tab.build')} {__BUILD_ID__}
          </div>
        </div>
      )}

      {/* 內容區（底部要留位畀 tab bar） */}
      <div style={{ maxWidth: '1080px', margin: '0 auto', padding: '12px 16px 0' }}>
        {children}
        <div style={TAB_BAR_SPACER} />
      </div>

      <RoleTabBar
        tabs={tabs}
        activeId={active}
        onSelect={(id) => {
          const target = tabs.find((tb) => tb.id === id);
          if (target && target.to !== pathname) navigate(target.to);
        }}
      />
    </>
  );
}
